import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Clock, X } from "lucide-react";
import { client } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Crest } from "./Crest";
import { PlayerAvatar } from "./PlayerAvatar";

const PATH = { player: "player", team: "team", competition: "competition" };

// Live search box with debounced autocomplete. Teams, players and competitions
// surface as the fan types; Enter (with nothing highlighted) runs a full search.
// When a signed-in fan focuses the empty box, their last few searches appear.
export const SearchAutocomplete = ({
  placeholder = "Search teams, players…",
  autoFocus = false,
  inputClassName = "",
  iconClassName = "",
  wrapperClassName = "",
  onNavigate,
}) => {
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [recents, setRecents] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const acRef = useRef(null);

  const showRecents = q.trim().length < 2;
  const list = showRecents ? recents : items;
  const visible = open && list.length > 0;

  const loadRecents = useCallback(async () => {
    if (!user) { setRecents([]); return; }
    try {
      const { data } = await client.get("/recent-searches");
      setRecents(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [user]);

  const record = useCallback(async (entry) => {
    if (!user) return;
    try {
      const { data } = await client.post("/recent-searches", entry);
      if (data?.items) setRecents(data.items);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { loadRecents(); }, [loadRecents]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setItems([]); return; }
    const t = setTimeout(async () => {
      acRef.current?.abort?.();
      const ac = new AbortController();
      acRef.current = ac;
      try {
        const { data } = await client.get(`/suggest?q=${encodeURIComponent(term)}`, { signal: ac.signal });
        setItems(data || []);
        setOpen(true);
        setActive(-1);
      } catch {
        /* aborted or failed - ignore, submit still works */
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = useCallback((item) => {
    setOpen(false); setQ("");
    record({
      type: item.type, label: item.name,
      slug: item.slug ? String(item.slug) : null,
      item_id: item.id != null ? String(item.id) : null,
      image: item.image || null,
    });
    onNavigate?.();
    nav(`/${PATH[item.type]}/${item.slug || item.id}`);
  }, [nav, onNavigate, record]);

  const goSearch = () => {
    const term = q.trim();
    if (term.length < 2) return;
    setOpen(false);
    record({ type: "query", label: term });
    onNavigate?.();
    nav(`/search?q=${encodeURIComponent(term)}`);
  };

  const goRecent = (it) => {
    setOpen(false); setQ("");
    record({ type: it.type, label: it.label, slug: it.slug, item_id: it.item_id, image: it.image });
    onNavigate?.();
    if (it.type === "query") nav(`/search?q=${encodeURIComponent(it.label)}`);
    else nav(`/${PATH[it.type]}/${it.slug || it.item_id}`);
  };

  const clearRecents = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setRecents([]);
    setOpen(false);
    try { await client.delete("/recent-searches"); } catch { /* ignore */ }
  };

  const onFocus = async () => {
    if (!showRecents && items.length) { setOpen(true); return; }
    await loadRecents();
    setOpen(true);
  };

  const onKey = (e) => {
    if (!visible) { if (e.key === "Enter") { e.preventDefault(); goSearch(); } return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0) { showRecents ? goRecent(list[active]) : go(list[active]); }
      else goSearch();
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className={`relative ${wrapperClassName}`} data-testid="search-autocomplete">
      <Search className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-slate-400 ${iconClassName}`} />
      <input
        data-testid="autocomplete-input"
        autoFocus={autoFocus}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKey}
        onFocus={onFocus}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={visible}
        aria-autocomplete="list"
        className={inputClassName}
      />

      {visible && showRecents && (
        <div data-testid="recent-searches-list"
          className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 text-left shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
          <div className="flex items-center justify-between px-4 pb-1.5 pt-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Recent searches</span>
            <button type="button" data-testid="clear-recent-searches"
              onMouseDown={clearRecents}
              className="text-[11px] font-semibold text-slate-400 transition-colors duration-150 hover:text-ink">
              Clear
            </button>
          </div>
          <ul className="max-h-80 overflow-auto">
            {recents.map((it, i) => (
              <li key={`${it.type}-${it.slug || it.item_id || it.label}-${i}`}>
                <button
                  type="button"
                  data-testid={`recent-item-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => goRecent(it)}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 ${active === i ? "bg-slate-50" : "hover:bg-slate-50"}`}
                >
                  {it.type === "query"
                    ? <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100"><Clock className="h-3.5 w-3.5 text-slate-400" /></span>
                    : it.type === "player"
                      ? <PlayerAvatar src={it.image} name={it.label} size={28} />
                      : <Crest src={it.image} alt={it.label} size={28} />}
                  <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">{it.label}</span>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate-300">{it.type === "query" ? "Search" : it.type}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {visible && !showRecents && (
        <ul data-testid="autocomplete-list"
          className="absolute left-0 right-0 z-50 mt-2 max-h-80 overflow-auto rounded-2xl border border-slate-200 bg-white py-2 text-left shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
          {items.map((it, i) => (
            <li key={`${it.type}-${it.id}`}>
              <button
                type="button"
                data-testid={`suggest-${it.type}-${it.id}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(it)}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 ${active === i ? "bg-slate-50" : "hover:bg-slate-50"}`}
              >
                {it.type === "player"
                  ? <PlayerAvatar src={it.image} name={it.name} size={28} />
                  : <Crest src={it.image} alt={it.name} size={28} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">{it.name}</span>
                  {it.sub && <span className="block truncate text-xs text-slate-400">{it.sub}</span>}
                </span>
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate-300">{it.type}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
